/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-06 08:34:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, TextStyle, Link, Button, Stack } from '@shopify/polaris';

import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderLabelTooltip } from 'src/components/views/common/forms/render-label-tooltip';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import RenderColorPickerHexPopover from 'src/components/views/common/render-color-picker-hex-popover';
import CustomDropZoneStandard from 'src/components/views/common/custom-dropzone-standard';
import {
  LABEL_POSITION,
  LABEL_TYPE,
  SALE_LABEL_DISPLAY,
  SOLD_OUT_LABEL_DISPLAY
} from 'src/constants/theme-settings';
import { URL } from 'src/constants/links';
import { buildOptionList } from 'src/helpers';

import * as filesActions from 'actions/files-actions';

type Props = WithI18nProps;

type State = {
  showSaleLabelSection: boolean,
  showSoldOutLabelSection: boolean,
  showLabelByTagSection: boolean,
  labelPosition: Array<{ value: string, label: string }>,
  labelType: Array<{ value: string, label: string }>,
  saleLabelDisplayOptions: Array<{ value: string, label: string }>,
  soldOutDisplayOptions: Array<{ value: string, label: string }>,
  saleImage: Object,
  soldOutImage: Object
};

class ProductLabelTab extends Component<Props, State> {
  state = {
    showSaleLabelSection: true,
    showSoldOutLabelSection: true,
    showLabelByTagSection: true,
    labelPosition: [],
    labelType: [],
    saleLabelDisplayOptions: [],
    soldOutDisplayOptions: [],
    saleImage: {},
    soldOutImage: {}
  };

  componentDidMount() {
    const { i18n } = this.props;

    const labelPosition = buildOptionList(LABEL_POSITION, i18n);
    const labelType = buildOptionList(LABEL_TYPE, i18n);
    const saleLabelDisplayOptions = buildOptionList(SALE_LABEL_DISPLAY, i18n);
    const soldOutDisplayOptions = buildOptionList(SOLD_OUT_LABEL_DISPLAY, i18n);
    this.setState({
      labelPosition,
      labelType,
      saleLabelDisplayOptions,
      soldOutDisplayOptions
    });
  }

  componentWillReceiveProps(nextProps) {
    const { i18n } = nextProps;
    const { i18n: prevI18n } = this.props;

    if (i18n.locale !== prevI18n.locale) {
      const labelPosition = buildOptionList(LABEL_POSITION, i18n);
      const labelType = buildOptionList(LABEL_TYPE, i18n);
      const saleLabelDisplayOptions = buildOptionList(SALE_LABEL_DISPLAY, i18n);
      const soldOutDisplayOptions = buildOptionList(SOLD_OUT_LABEL_DISPLAY, i18n);
      this.setState({
        labelPosition,
        labelType,
        saleLabelDisplayOptions,
        soldOutDisplayOptions
      });
    }
  }

  render() {
    const {
      i18n,
      updateField,
      saleLabelEnable,
      saleLabelDisplay,
      saleLabelImg,
      saleLabelImgSize,
      saleLabelBg,
      saleLabelText,
      soldOutEnable,
      soldOutDisplay,
      soldOutImg,
      soldOutImgSize,
      soldOutBg,
      soldOutText,
      tagLabelEnable,
      tagLabelBg,
      tagLabelText
    } = this.props;
    const {
      showSaleLabelSection,
      showSoldOutLabelSection,
      showLabelByTagSection,
      labelPosition,
      labelType,
      saleLabelDisplayOptions,
      soldOutDisplayOptions
    } = this.state;

    return (
      <FormLayout>
        <TextStyle variation="subdued">
          {i18n.translate('AdminHTML.DefaultThemeSettings.ProductLabelTab.helperText', {
            t1: (
              <Link external url={`${URL['DefaultThemeSettingsDoc']}#a2.4`}>
                {i18n.translate('AdminHTML.DefaultThemeSettings.ProductLabelTab.helperText_t1')}
              </Link>
            )
          })}
        </TextStyle>

        <div className="groupFields">
          <div className="groupFields-flex-2">
            <Field
              name="products.labelPosition"
              component={RenderSelect}
              label={i18n.translate('AdminHTML.DefaultThemeSettings.ProductLabelTab.field1_Label')}
              options={labelPosition}
            />
            <Field
              name="products.labelType"
              component={RenderSelect}
              label={i18n.translate('AdminHTML.DefaultThemeSettings.ProductLabelTab.field2_Label')}
              options={labelType}
            />
          </div>
        </div>

        <div className="breakLine" />

        {/* Sale label */}
        <div className="groupFields-label">
          <Button
            disclosure={showSaleLabelSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showSaleLabelSection')}>
            <RenderLabelTooltip
              label={
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ProductLabelTab.gFSaleLabel_Label'
                  )}
                </TextStyle>
              }
            />
          </Button>
        </div>

        {showSaleLabelSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <Field
                  name="products.saleLabelEnable"
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ProductLabelTab.field3_Label'
                  )}
                  tooltip={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ProductLabelTab.gFSaleLabel_Tooltip'
                  )}
                />
                <div className="render-choice-flex-3">
                  <Field
                    name="products.saleLabelDisplay"
                    component={RenderChoice}
                    label={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field4_Label'
                    )}
                    choices={saleLabelDisplayOptions}
                    isDisabled={!saleLabelEnable}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={saleLabelBg}
                    fieldName="products.saleLabelBg"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field5_Label'
                    )}
                    updateField={updateField}
                    disabled={!saleLabelEnable || saleLabelDisplay[0] !== 'text'}
                  />

                  <RenderColorPickerHexPopover
                    colorValue={saleLabelText}
                    fieldName="products.saleLabelText"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field6_Label'
                    )}
                    updateField={updateField}
                    disabled={!saleLabelEnable || saleLabelDisplay[0] !== 'text'}
                  />
                </div>

                {/* IMAGE UPLOAD */}
                <div>
                  {i18n.translate('AdminHTML.DefaultThemeSettings.ProductLabelTab.field7_Label')}
                </div>
                <div className="BC-DefaultThemeSettings_Dropzone">
                  <Field
                    name="products.saleLabelImg"
                    component={CustomDropZoneStandard}
                    disabled={saleLabelDisplay[0] !== 'image'}
                    allowMultiple={false}
                    onSelectFile={this.onSelectSaleImage}
                    accept={'image/*'}
                    fileTypes={['image/png', 'image/jpeg']}
                    defaultFile={saleLabelImg}
                    defaultFileSize={saleLabelImgSize}
                  />
                </div>
              </Stack>
            </div>
          </div>
        )}

        <div className="breakLine" />

        {/* Sold out */}
        <div className="groupFields-label">
          <Button
            disclosure={showSoldOutLabelSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showSoldOutLabelSection')}>
            <RenderLabelTooltip
              label={
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ProductLabelTab.gFSoldOutLabel_Label'
                  )}
                </TextStyle>
              }
            />
          </Button>
        </div>

        {showSoldOutLabelSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <Field
                  name="products.soldOutEnable"
                  component={RenderCheckbox}
                  label={i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ProductLabelTab.field8_Label'
                  )}
                />
                <div className="render-choice-flex-3">
                  <Field
                    name="products.soldOutDisplay"
                    component={RenderChoice}
                    label={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field9_Label'
                    )}
                    choices={soldOutDisplayOptions}
                    isDisabled={!soldOutEnable}
                  />
                </div>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={soldOutBg}
                    fieldName="products.soldOutBg"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field10_Label'
                    )}
                    disabled={!soldOutEnable || soldOutDisplay[0] !== 'text'}
                    updateField={updateField}
                  />

                  <RenderColorPickerHexPopover
                    colorValue={soldOutText}
                    fieldName="products.soldOutText"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field11_Label'
                    )}
                    disabled={!soldOutEnable || soldOutDisplay[0] !== 'text'}
                    updateField={updateField}
                  />
                </div>

                {/* IMAGE UPLOAD */}
                <div>
                  {i18n.translate('AdminHTML.DefaultThemeSettings.ProductLabelTab.field12_Label')}
                </div>
                <div className="BC-DefaultThemeSettings_Dropzone">
                  <Field
                    name="products.soldOutImg"
                    component={CustomDropZoneStandard}
                    disabled={soldOutDisplay[0] !== 'image'}
                    allowMultiple={false}
                    onSelectFile={this.onSelectSoldOutImage}
                    accept={'image/*'}
                    fileTypes={['image/png', 'image/jpeg']}
                    defaultFile={soldOutImg}
                    defaultFileSize={soldOutImgSize}
                  />
                </div>
              </Stack>
            </div>
          </div>
        )}

        <div className="breakLine" />

        {/* Custom label by tag */}
        <div className="groupFields-label">
          <Button
            disclosure={showLabelByTagSection ? 'down' : 'up'}
            plain
            monochrome
            removeUnderline
            onClick={() => this.toggleState('showLabelByTagSection')}>
            <RenderLabelTooltip
              label={
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.DefaultThemeSettings.ProductLabelTab.gFCustomLabelByTag_Label'
                  )}
                </TextStyle>
              }
            />
          </Button>
        </div>

        {showLabelByTagSection && (
          <div className="groupFields-content">
            <div style={{ marginTop: '-2rem' }}>
              <Stack vertical>
                <div>
                  <Field
                    name="products.tagLabelEnable"
                    component={RenderCheckbox}
                    label={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field13_Label'
                    )}
                  />
                  <div className="subText">
                    <TextStyle variation="subdued">
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.ProductLabelTab.gFCustomLabelByTag_Note',
                        {
                          t1: (
                            <Link external url={URL['DefaultThemeSettingsCustomLabelByTag']}>
                              {i18n.translate(
                                'AdminHTML.DefaultThemeSettings.ProductLabelTab.gFCustomLabelByTag_Note_t1'
                              )}
                            </Link>
                          )
                        }
                      )}
                    </TextStyle>
                  </div>
                </div>
                <div className="groupFields-flex-2">
                  <RenderColorPickerHexPopover
                    colorValue={tagLabelBg}
                    fieldName="products.tagLabelBg"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field14_Label'
                    )}
                    disabled={!tagLabelEnable}
                    updateField={updateField}
                  />
                  <RenderColorPickerHexPopover
                    colorValue={tagLabelText}
                    fieldName="products.tagLabelText"
                    fieldLabel={i18n.translate(
                      'AdminHTML.DefaultThemeSettings.ProductLabelTab.field15_Label'
                    )}
                    disabled={!tagLabelEnable}
                    updateField={updateField}
                  />
                </div>
              </Stack>
            </div>
          </div>
        )}
      </FormLayout>
    );
  }

  toggleState = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  onSelectSaleImage = files => {
    this.setState({ saleImage: files[0] });
    this.props.updateField('products.saleLabelImg', files[0]);
  };

  onSelectSoldOutImage = files => {
    this.setState({ soldOutImage: files[0] });
    this.props.updateField('products.soldOutImg', files[0]);
  };
}

const mapStateToProps = (state, props) => {
  const defaultThemeSettingsForm = getFormValues('DefaultThemeSettingsForm')(state);
  const { defaultThemeSettings } = state;

  let colorList = {};
  ['saleLabelBg', 'saleLabelText', 'soldOutBg', 'soldOutText', 'tagLabelBg', 'tagLabelText'].map(
    item => {
      colorList[item] = _.get(defaultThemeSettingsForm, `products.${item}`, '#FFFFFF');
      return '';
    }
  );

  return {
    ...colorList,
    loading: _.get(defaultThemeSettings, 'loadingDefaultThemeSettings', false),
    saleLabelEnable: _.get(defaultThemeSettingsForm, 'products.saleLabelEnable', true),
    saleLabelDisplay: _.get(defaultThemeSettingsForm, 'products.saleLabelDisplay', ['text']),
    saleLabelImg: _.get(defaultThemeSettingsForm, 'products.saleLabelImg', {}),
    saleLabelImgSize: _.get(defaultThemeSettingsForm, 'products.saleLabelImgSize', 0),
    soldOutEnable: _.get(defaultThemeSettingsForm, 'products.soldOutEnable', true),
    soldOutDisplay: _.get(defaultThemeSettingsForm, 'products.soldOutDisplay', ['text']),
    soldOutImg: _.get(defaultThemeSettingsForm, 'products.soldOutImg', {}),
    soldOutImgSize: _.get(defaultThemeSettingsForm, 'products.soldOutImgSize', 0),
    tagLabelEnable: _.get(defaultThemeSettingsForm, 'products.tagLabelEnable', true)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateField: (id, data) => dispatch(change(`DefaultThemeSettingsForm`, id, data)),
  uploadFiles: () => dispatch(filesActions.uploadFiles(ownProps.i18n))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(ProductLabelTab));
